import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Loader2, Send, X } from 'lucide-react';

export type PendingAttachment = {
  id: string;
  file: File;
  previewUrl: string;
  type: 'image' | 'pdf' | 'other';
};

export type AttachmentPreviewProps = {
  attachment: PendingAttachment;
  onSend: (caption: string) => Promise<void> | void;
  onCancel: () => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Overlay de confirmacao pre-envio de anexo (estilo WhatsApp/Telegram).
// Mobile: fullscreen sobre fundo preto; desktop: modal centralizado branco.
// Caption opcional, Enter envia, Esc cancela, X cleanup obrigatorio de
// URL.createObjectURL na pagina pai.
export const AttachmentPreview = ({ attachment, onSend, onCancel }: AttachmentPreviewProps) => {
  const [caption, setCaption] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await onSend(caption.trim());
    } catch {
      // Erro reportado via toast no parent; mantemos preview aberto.
      setSending(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!sending) onCancel();
    }
  };

  const handleBackdropClick = () => {
    if (!sending) onCancel();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Preview de anexo"
      className="fixed inset-0 z-50 flex animate-in fade-in duration-150 md:items-center md:justify-center md:bg-black/60"
      onClick={handleBackdropClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col bg-[#1F1F1F] md:h-auto md:max-h-[85vh] md:w-full md:max-w-md md:overflow-hidden md:rounded-2xl md:bg-white md:shadow-2xl"
      >
        <header className="flex items-center gap-3 px-3 py-3 text-white md:bg-white md:px-4 md:text-gray-900">
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            aria-label="Cancelar"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/10 disabled:opacity-50 md:hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{attachment.file.name}</p>
            <p className="truncate text-xs text-white/60 md:text-gray-500">
              {formatFileSize(attachment.file.size)}
            </p>
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center overflow-hidden bg-[#1F1F1F] p-4 md:min-h-[280px] md:bg-gray-50">
          {attachment.type === 'image' ? (
            <img
              src={attachment.previewUrl}
              alt="Preview do anexo"
              className="max-h-full max-w-full rounded-lg object-contain"
            />
          ) : (
            <div className="flex max-w-xs flex-col items-center gap-3 rounded-2xl bg-white px-6 py-8 text-center shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                <FileText className="h-7 w-7 text-gray-500" />
              </div>
              <div>
                <p className="max-w-[200px] truncate text-sm font-semibold text-gray-900">
                  {attachment.file.name}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {formatFileSize(attachment.file.size)}
                </p>
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-end gap-2 border-t border-white/10 bg-[#1F1F1F] px-3 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+10px)] md:border-gray-100 md:bg-white md:pb-3">
          <textarea
            ref={textareaRef}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Adicione uma legenda"
            rows={1}
            maxLength={500}
            disabled={sending}
            aria-label="Legenda do anexo"
            className="max-h-24 flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-white/20 focus:outline-none disabled:opacity-50 md:border-gray-200 md:bg-gray-50 md:text-gray-900 md:placeholder:text-gray-400 md:focus:border-gray-300"
          />
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={sending}
            aria-label="Enviar"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-gray-900 transition-transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 md:bg-gray-900 md:text-white"
          >
            {sending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};
